// src/prf/prfIndexMethodology.js
// The PRF final grid index, defined once, for both methodologies.
//
// RMA changed the Rainfall Index precipitation source on 2026-08-31. That is not
// a swap of one number for another — the two methodologies compute the index a
// different way, from different inputs, over a different baseline period. So the
// methodology is a first-class value here, it travels with every figure, and
// mixing the two is refused rather than merely discouraged.
//
// ┌─ CPC_LEGACY ────────────────── crop years through 2026 ────────────────┐
// │ NOAA CPC gauge-interpolated 0.25 degree daily precipitation.           │
// │ Historical CPC data since 1948 (FCIC-18150 para 33).                   │
// │ RMA does not restate published CPC-era indices, so these stay valid.   │
// └───────────────────────────────────────────────────────────────────────┘
// ┌─ NCEI_MONTHLY ──────────────── crop years 2027 onward ─────────────────┐
// │ NOAA NCEI nClimGrid, RESCALED BY NCEI to the existing 0.25 degree PRF  │
// │ grid, delivered as RMA-specific MONTHLY FINAL CSV files.               │
// │ interval precipitation = sum of the applicable monthly values          │
// │ normal for crop year Y  = mean interval precipitation, 1951 .. Y-2     │
// │ final grid index        = interval precip / normal, rounded to 3 dp    │
// └───────────────────────────────────────────────────────────────────────┘
//
// ⚠ NEVER MIX. An NCEI numerator over a CPC denominator (or the reverse) is the
// defect this module exists to make impossible. It is not hypothetical: measured
// 2026-08-24 over June on five WNY grids, an NCEI daily aggregate ran ~7.5%
// wetter than CPC, one-signed on every grid. Divided by a CPC normal that is
// roughly 7-10 index POINTS of bias — every farm reading further from a trigger
// than it is. The index is a ratio; both halves must come from the same source.

// PRF has ELEVEN index intervals, 625..635 (JAN-FEB .. NOV-DEC). There is no
// DEC-JAN interval in PRF — RMA's own Decision Support Tool declares
// IntervalCodeStart 625 / IntervalCodeEnd 635 — so no interval spans a year
// boundary and "sum of the applicable monthly values" is always two months of
// the same calendar year.
const PRF_INTERVALS = {
  625: { code: "JANFEB", months: [1, 2] },
  626: { code: "FEBMAR", months: [2, 3] },
  627: { code: "MARAPR", months: [3, 4] },
  628: { code: "APRMAY", months: [4, 5] },
  629: { code: "MAYJUN", months: [5, 6] },
  630: { code: "JUNJUL", months: [6, 7] },
  631: { code: "JULAUG", months: [7, 8] },
  632: { code: "AUGSEP", months: [8, 9] },
  633: { code: "SEPOCT", months: [9, 10] },
  634: { code: "OCTNOV", months: [10, 11] },
  635: { code: "NOVDEC", months: [11, 12] },
};

const PRF_METHODOLOGY = {
  CPC_LEGACY: {
    id: "CPC_LEGACY",
    label: "NOAA CPC (through crop year 2026)",
    source: "NOAA Climate Prediction Center, 0.25 degree gauge-interpolated",
    cadence: "daily",
    normalFirstYear: 1948,
    lastCropYear: 2026,
    indexDecimals: 3,
  },
  NCEI_MONTHLY: {
    id: "NCEI_MONTHLY",
    label: "NOAA NCEI nClimGrid monthly (crop year 2027 onward)",
    source: "NOAA NCEI nClimGrid, rescaled by NCEI to the 0.25 degree PRF grid",
    cadence: "monthly",
    // RMA's 2027+ processing instructions: the normal is the mean interval
    // precipitation from 1951 through Y-2. 1951 is also where NCEI's own
    // nClimGrid-Daily archive begins, which is a consistency check, not a
    // coincidence to lean on.
    normalFirstYear: 1951,
    firstCropYear: 2027,
    indexDecimals: 3,
  },
  // Our own daily aggregation. NOT an RMA methodology and never a substitute for
  // one: it estimates an in-progress interval before monthly finals exist.
  // Anything derived from it must be labelled estimated on screen.
  NCEI_DAILY_ESTIMATE: {
    id: "NCEI_DAILY_ESTIMATE",
    label: "NCEI nClimGrid daily, Clover Ag 36-cell estimate",
    source: "NOAA NCEI nClimGrid-Daily, aggregated by Clover Ag (not by NCEI)",
    cadence: "daily",
    isEstimate: true,
    indexDecimals: 3,
  },
};

// Which methodology governs a crop year. Deliberately a lookup rather than a
// comparison scattered at call sites — the 2026/2027 boundary is the single
// place this changes and it should change once.
function methodologyForCropYear(cropYear) {
  const y = Number(cropYear);
  if (!Number.isFinite(y)) throw new Error(`methodologyForCropYear: bad crop year ${cropYear}`);
  return y >= PRF_METHODOLOGY.NCEI_MONTHLY.firstCropYear
    ? PRF_METHODOLOGY.NCEI_MONTHLY
    : PRF_METHODOLOGY.CPC_LEGACY;
}

// The years averaged to form the normal for crop year Y: firstYear .. Y-2.
// Y-2 and not Y-1 because the prior year's finals are not complete when the
// crop year is rated. Returns [] for a crop year before the record starts,
// which callers must treat as "no normal", never as a zero denominator.
function normalYears(cropYear, methodology) {
  const m = methodology || methodologyForCropYear(cropYear);
  const last = Number(cropYear) - 2;
  if (!Number.isFinite(last) || last < m.normalFirstYear) return [];
  const out = [];
  for (let y = m.normalFirstYear; y <= last; y++) out.push(y);
  return out;
}

// Interval precipitation = sum of the applicable monthly values.
// `monthly` is { month(1-12): precip }. A MISSING MONTH RETURNS null, never a
// partial sum: half an interval divided by a whole-interval normal renders as a
// drought that did not happen — the same trap the PRF map's mid-interval
// handling already guards.
function intervalPrecip(monthly, intervalCode) {
  const spec = PRF_INTERVALS[Number(intervalCode)];
  if (!spec) throw new Error(`intervalPrecip: ${intervalCode} is not a PRF interval (625..635)`);
  let sum = 0;
  for (const mo of spec.months) {
    const v = monthly?.[mo];
    if (v == null || !Number.isFinite(Number(v))) return null;
    sum += Number(v);
  }
  return sum;
}

// Mean interval precipitation across the normal years. Returns null rather than
// a thin average when years are missing — a normal built from a short record is
// a guess wearing a number, and it multiplies every index on that grid.
function intervalNormal(byYear, intervalCode, cropYear, methodology, { minYears = 30 } = {}) {
  const m = methodology || methodologyForCropYear(cropYear);
  const years = normalYears(cropYear, m);
  if (!years.length) return null;
  const vals = [];
  for (const y of years) {
    const p = intervalPrecip(byYear?.[y], intervalCode);
    if (p != null) vals.push(p);
  }
  if (vals.length < minYears) return null;
  return { normal: vals.reduce((a, b) => a + b, 0) / vals.length, nYears: vals.length, years };
}

// THE GUARD. Refuses a numerator and denominator from different methodologies.
// Throws rather than warns: a warning still reaches the page.
function assertSameMethodology(numeratorMethodology, denominatorMethodology, context = "") {
  const a = numeratorMethodology?.id || numeratorMethodology;
  const b = denominatorMethodology?.id || denominatorMethodology;
  if (!a || !b) throw new Error(`PRF index${context ? ` (${context})` : ""}: methodology missing on ${!a ? "numerator" : "denominator"}`);
  if (a !== b) {
    throw new Error(
      `PRF index${context ? ` (${context})` : ""}: refusing to divide a ${a} precipitation by a ${b} normal. ` +
      `The index is a ratio and both halves must come from the same source.`
    );
  }
}

// final grid index = interval precipitation / normal, rounded to 3 decimals,
// expressed as a FRACTION (1.000 = normal) exactly as RMA publishes it in
// Rainfall_Index_HistoricData{YYYY}CY. Callers wanting percent multiply by 100
// AFTER rounding — rounding a percent gives a different number.
function finalGridIndex({ precip, normal, methodology, normalMethodology, context = "" }) {
  assertSameMethodology(methodology, normalMethodology || methodology, context);
  if (precip == null || normal == null) return null;
  const n = Number(normal);
  if (!Number.isFinite(n) || n <= 0) return null;
  const dp = (methodology?.indexDecimals ?? 3);
  const raw = Number(precip) / n;
  // toFixed on a binary float can round a true .5 the wrong way; nudge by a
  // half-ulp of the target precision first. Same correction check-home-safe
  // needed when (5.085).toFixed(2) came out "5.08".
  const scale = Math.pow(10, dp);
  return Math.round(raw * scale + Number.EPSILON * raw * scale) / scale;
}

// Payment factor and indemnity, unchanged across methodologies — verified
// against RMA's own pricing engine 2026-08-24 (grid 27508, 85% coverage,
// index 0.722 → factor 0.150588 → $4,291 on $28,497 of protection).
function paymentFactor(coverageLevel, finalIndex) {
  const c = Number(coverageLevel), i = Number(finalIndex);
  if (!Number.isFinite(c) || !Number.isFinite(i) || c <= 0) return null;
  return i >= c ? 0 : (c - i) / c;
}

module.exports = {
  PRF_INTERVALS,
  PRF_METHODOLOGY,
  methodologyForCropYear,
  normalYears,
  intervalPrecip,
  intervalNormal,
  assertSameMethodology,
  finalGridIndex,
  paymentFactor,
};
